import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { useCart } from '../context/CartContext';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CartModal } from './CartModal';
import { useNavigate } from 'react-router-dom';
import { TrainingPlanWarningModal } from './TrainingPlanWarningModal';
import { CountdownTimer } from './CountdownTimer';
import { ClientTransformationShowcase } from './ClientTransformationShowcase';
import { trackAddToCart } from '../utils/analytics';
interface PlanLevel {
  id: string;
  title: string;
  emoji: string;
  shortDescription: string;
  bulletPoints: string[];
  fullDescription: string;
  priceOneMonth: number;
  priceThreeMonths: number;
}
interface TrainingPlansSectionProps {
  showProgressSection?: boolean;
}
const BhsExplainer: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-1250">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full group" data-id="element-1251">
        <div className="relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-sm rounded-xl border border-gray-700/50 hover:border-green-500/40 transition-all duration-500 overflow-hidden px-5 py-4 md:px-7 md:py-5" data-id="element-1252">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-green-500/30 to-transparent" data-id="element-1253" />
          <div className="flex items-center justify-between" data-id="element-1254">
            <span className="text-sm md:text-lg font-bold text-white tracking-wide" data-id="element-1255">
              Co je <span className="text-green-500" data-id="element-1256">BHS</span> ve zkratce?
            </span>
            <motion.div animate={{
            rotate: isOpen ? 180 : 0
          }} transition={{
            duration: 0.3,
            ease: 'easeInOut'
          }} className="flex-shrink-0 ml-4" data-id="element-1257">
              <ChevronDownIcon className="w-5 h-5 text-green-500" data-id="element-1258" />
            </motion.div>
          </div>
        </div>
      </button>

      <AnimatePresence data-id="element-1259">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="overflow-hidden" data-id="element-1260">
            <div className="relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm rounded-b-xl border border-t-0 border-gray-700/50 px-5 py-5 md:px-7 md:py-6 -mt-3 pt-7" data-id="element-1261">
              <div className="space-y-4 text-sm md:text-base text-gray-300 leading-relaxed" data-id="element-1262">
                <p data-id="element-1263">
                  BHS je{' '}
                  <strong className="text-white" data-id="element-1264">BODY HACK SYSTEM</strong>. Je
                  to <strong className="text-white" data-id="element-1265">kompletní systém</strong>,
                  který spojuje trénink i stravu do{' '}
                  <strong className="text-white" data-id="element-1266">
                    jednoho jasného postupu
                  </strong>
                  .
                </p>
                <p data-id="element-1267">
                  Ukáže ti přesně,{' '}
                  <strong className="text-white" data-id="element-1268">jak cvičit</strong>,{' '}
                  <strong className="text-white" data-id="element-1269">jak jíst</strong> a{' '}
                  <strong className="text-white" data-id="element-1270">
                    jak růst co nejrychleji
                  </strong>
                  , aniž bys <strong className="text-white" data-id="element-1271">ztrácel čas</strong>{' '}
                  <strong className="text-white" data-id="element-1272">metodou pokus–omyl</strong>. ⚡
                </p>
                <p data-id="element-1273">
                  Je to tvůj{' '}
                  <strong className="text-green-400" data-id="element-1274">
                    fast track k vysněné postavě
                  </strong>{' '}
                  — systém, který tě{' '}
                  <strong className="text-white" data-id="element-1275">krok za krokem</strong> dovede{' '}
                  <strong className="text-white" data-id="element-1276">k výsledkům</strong> a pomůže
                  ti{' '}
                  <strong className="text-white" data-id="element-1277">
                    využít tvůj potenciál na maximum
                  </strong>
                  . 🚀
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const TrainingPlansSection: React.FC<TrainingPlansSectionProps> = ({
  showProgressSection = false
}) => {
  const {
    addItem,
    cartModalOpen,
    setCartModalOpen,
    lastAddedItem,
    trainingPlanWarningOpen,
    setTrainingPlanWarningOpen,
    existingTrainingPlanName
  } = useCart();
  const navigate = useNavigate();
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>({});
  const [selectedDurations, setSelectedDurations] = useState<Record<string, '1' | '3'>>({
    beginner: '3',
    advanced: '3'
  });
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const togglePlanDetails = (planId: string) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };
  const handleDurationChange = (planId: string, duration: '1' | '3') => {
    setSelectedDurations(prev => ({
      ...prev,
      [planId]: duration
    }));
  };
  const handleAddToCart = (plan: PlanLevel) => {
    const duration = selectedDurations[plan.id];
    const price = duration === '1' ? plan.priceOneMonth : plan.priceThreeMonths;
    const itemId = `training-${plan.id}-${duration}`;
    const itemName = `Tréninkový plán - ${plan.title} (${duration === '1' ? '1 měsíc' : '3 měsíce'})`;
    // Track AddToCart event
    trackAddToCart({
      id: itemId,
      name: itemName,
      price: price,
      category: 'training_plan'
    });
    addItem({
      id: itemId,
      name: itemName,
      price,
      type: 'training',
      details: `${plan.title}, ${duration === '1' ? '1 měsíc' : '3 měsíce'}`
    });
    navigate('/cart');
  };
  const handleContinueShopping = () => {
    setCartModalOpen(false);
  };
  const handleGoToCart = () => {
    setCartModalOpen(false);
    navigate('/cart');
  };
  const scrollToSuperfoods = () => {
    navigate('/superpotraviny');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };
  const plans: PlanLevel[] = [{
    id: 'beginner',
    title: 'BHS SYSTÉM',
    emoji: '💪',
    shortDescription: '',
    bulletPoints: ['Videoukázky ke každému cviku (správná technika + tempo)', 'Split: 4 tréninky / 3 dny odpočinku', 'Přesně dané série, opakování a pauzy', 'Systém zapisování a sledování progresu', 'Jasně strukturovaný progresivní overload', 'Využití začátečnického potenciálu pro rychlé výsledky', 'Správná délka pauz mezi sériemi pro maximální růst', 'Plán postavený na základě desítek studií + letech zkušeností', 'Plán, který ti dá know-how na to jak správně cvičit', 'Každé 2 týdny 1:1 online check-up call pro kontrolu tvého progresu'],
    fullDescription: 'Celý plán je navržený tak, aby si co nejlépe využil tzv. začátečníký potenciál – tedy období, kdy tělo roste nejrychleji a výsledky jdou vidět téměř okamžitě. Zároveň ale dbáme na to, abys ses nepřetrénoval/a – svaly potřebují čas na regeneraci. A právě regeneraci plán zohledňuje, tak aby sis mohl/a budovat formu bezpečně a efektivně. Nemusíš se ničeho bát – všechno ti ukážeme krok po kroku. Stačí nabrat odvahu a konečně se rozhodnout se změnit jak fyzicky, tak psychicky. Možná právě v tomhle tréninkovém plánu, se skrývá klíč k sebevědomí a změně sebe, po které toužíš. Stačí se rozhodnout investovat do sebe, udělat první krok – jakmile uvidíš reálné výsledky pak už tě nic nezastaví. Stačí začít!',
    priceOneMonth: 899,
    priceThreeMonths: 1799
  }];
  return <section className="section section-dark relative py-16" id="training" data-id="element-1278">
      <CartModal isOpen={cartModalOpen} onClose={handleContinueShopping} onGoToCart={handleGoToCart} onContinueShopping={handleContinueShopping} productName={lastAddedItem || ''} data-id="element-1279" />

      <TrainingPlanWarningModal isOpen={trainingPlanWarningOpen} onClose={() => setTrainingPlanWarningOpen(false)} existingPlanName={existingTrainingPlanName || ''} data-id="element-1280" />

      <div className="container mx-auto px-4 section-content" data-id="element-1281">
        <div className="flex justify-center mb-8 md:mb-12" data-id="element-1282">
          <motion.h2 initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="text-3xl md:text-6xl font-black section-header-accent text-center" data-id="element-1283">
            <span className="highlight-text" data-id="element-1284">CENÍK</span> TRÉNINKOVÝCH SYSTÉMŮ
          </motion.h2>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} viewport={{
        once: true
      }} className="mb-8 md:mb-10" data-id="element-1285">
          <div className="relative max-w-4xl mx-auto" data-id="element-1286">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vwb5egz7newgdqpuo6r6qu/4a086d28-6024-43ad-b7cb-63b8ce93da6c/791f6e22-5e9c-4f05-a0eb-997119d00581.png" alt="Aplikace Trainerize na mobilních telefonech" className="w-full h-auto object-contain rounded-xl shadow-2xl" data-id="element-1287" />

            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent rounded-xl flex items-end" data-id="element-1288">
              <div className="p-4 md:p-6" data-id="element-1289">
                <h3 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-glow" data-id="element-1290">
                  Přehledná aplikace pro tvůj trénink
                </h3>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto mb-8 md:mb-12 text-center" data-id="element-1291"></motion.div>

        <div id="plan-cards" data-id="element-1292">
          <CountdownTimer data-id="element-1293" />
        </div>

        <div className="grid grid-cols-1 gap-4 md:gap-6 training-plans-cards max-w-lg mx-auto" data-id="element-1294">
          {plans.map((plan, index) => <motion.div key={plan.id} className="premium-card flex flex-col h-full bg-gray-900/80 border border-gray-800" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.2
        }} viewport={{
          once: true
        }} whileHover={{
          y: -5,
          boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.3)',
          borderColor: 'rgba(34, 197, 94, 0.3)'
        }} data-id="element-1295">
              <div className="p-4 md:p-5 border-b border-gray-800 relative" data-id="element-1296">
                <div className="flex items-center justify-between" data-id="element-1297">
                  <h3 className="text-lg md:text-xl font-black text-green-500 flex items-center" data-id="element-1298">
                    {plan.title}
                  </h3>
                  <AnimatePresence data-id="element-1299">
                    {plan.id === 'beginner' && selectedDurations[plan.id] === '3' && <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} transition={{
                  duration: 0.2
                }} className="relative ml-3" data-id="element-1300">
                          <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-500 rounded-full blur-sm opacity-60 animate-pulse" data-id="element-1301"></div>
                          <div className="relative bg-gradient-to-r from-green-600 to-green-500 text-white text-[9px] md:text-[11px] font-black px-2.5 md:px-3 py-1 md:py-1.5 rounded-full shadow-lg border border-white/30" data-id="element-1302">
                            <span className="relative z-10 tracking-wider whitespace-nowrap" data-id="element-1303">
                              NEJPRODÁVANĚJŠÍ
                            </span>
                          </div>
                        </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
              <div className="p-4 md:p-5 flex-1 flex flex-col" data-id="element-1304">
                <p className="text-gray-300 text-xs md:text-sm mb-3" data-id="element-1305">
                  {plan.shortDescription}
                </p>
                <div className="flex-1 flex flex-col" data-id="element-1306">
                  <ul className="space-y-3 mb-4 flex-1" data-id="element-1307">
                    {plan.bulletPoints.map((point, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-1308">
                        <div className="flex-shrink-0 mt-0.5" data-id="element-1309">
                          <CheckIcon className="w-5 h-5 text-green-500" strokeWidth={2.5} data-id="element-1310" />
                        </div>
                        <span className="text-gray-200 text-sm md:text-base font-medium leading-relaxed" data-id="element-1311">
                          {point}
                        </span>
                      </li>)}
                  </ul>
                  <motion.button onClick={() => togglePlanDetails(plan.id)} className="flex items-center text-green-500 hover:text-green-400 transition-colors text-xs font-bold mb-4" whileHover={{
                x: 5
              }} transition={{
                duration: 0.2
              }} data-id="element-1312">
                    {expandedPlans[plan.id] ? <>
                        <span data-id="element-1313">MÉNĚ INFORMACÍ</span>
                        <ChevronUpIcon className="w-4 h-4 ml-1" data-id="element-1314" />
                      </> : <>
                        <span data-id="element-1315">VÍCE INFORMACÍ</span>
                        <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-1316" />
                      </>}
                  </motion.button>
                  <motion.div initial={false} animate={{
                height: expandedPlans[plan.id] ? 'auto' : 0,
                opacity: expandedPlans[plan.id] ? 1 : 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-1317">
                    {expandedPlans[plan.id] && <div className="mt-2 text-gray-300 bg-gray-800 p-4 rounded-lg text-xs mb-4" data-id="element-1318">
                        <p className="whitespace-pre-line" data-id="element-1319">
                          {plan.fullDescription}
                        </p>
                      </div>}
                  </motion.div>
                  <div className="flex justify-center mb-4 bg-gray-800 rounded-md overflow-hidden" data-id="element-1320">
                    <button onClick={() => handleDurationChange(plan.id, '1')} className={`flex-1 py-3 text-center font-bold transition-colors text-xs ${selectedDurations[plan.id] === '1' ? 'bg-gray-700 text-white' : 'bg-transparent text-gray-400 hover:bg-gray-700/50'}`} data-id="element-1321">
                      1 měsíc
                    </button>
                    <button onClick={() => handleDurationChange(plan.id, '3')} className={`flex-1 py-3 text-center font-bold transition-colors text-xs ${selectedDurations[plan.id] === '3' ? 'bg-green-600 text-white' : 'bg-transparent text-gray-400 hover:bg-gray-700/50'}`} data-id="element-1322">
                      3 měsíce
                    </button>
                  </div>
                  <div className="text-center mb-4 md:mb-6" data-id="element-1323">
                    <div className="text-2xl md:text-4xl font-black text-green-500 mb-2 drop-shadow-[0_0_10px_rgba(34,197,94,0.3)]" data-id="element-1324">
                      {selectedDurations[plan.id] === '1' ? `${plan.priceOneMonth} Kč` : `${plan.priceThreeMonths} Kč`}
                    </div>
                    {selectedDurations[plan.id] === '3' && <div className="relative inline-block bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/20 rounded-lg px-4 py-2 mb-2 backdrop-blur-sm" data-id="element-1325">
                        <div className="text-xs md:text-sm text-gray-300 flex items-center justify-center gap-2" data-id="element-1326">
                          <span className="text-gray-500 line-through decoration-red-500/40 decoration-1" data-id="element-1327">
                            {plan.priceOneMonth * 3} Kč
                          </span>
                          <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300 uppercase tracking-wide" data-id="element-1328">
                            Ušetříš{' '}
                            {plan.priceOneMonth * 3 - plan.priceThreeMonths} Kč
                          </span>
                        </div>
                      </div>}
                    <div className="flex items-center justify-center gap-2 text-sm md:text-base mt-2" data-id="element-1329">
                      <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-300 to-green-400 tracking-wide drop-shadow-[0_0_8px_rgba(34,197,94,0.4)]" data-id="element-1330">
                        Jednou zaplatíš a už nikdy nebudeš muset platit znovu!
                      </span>
                    </div>
                  </div>
                  <button onClick={() => handleAddToCart(plan)} className="bg-green-600 text-white py-3 md:py-4 rounded-md font-bold flex items-center justify-center hover:bg-green-700 transition-colors text-xs md:text-base" data-id="element-1331">
                    <CheckIcon className="w-4 h-4 md:w-5 md:h-5 mr-2" data-id="element-1332" />
                    PŘIDAT DO KOŠÍKU
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>

        <div className="mt-8 md:mt-12" data-id="element-1333">
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8" data-id="element-1334">
            <div className="flex items-center" data-id="element-1335">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1336" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-1337">
                Přehledná aplikace
              </span>
            </div>
            <div className="flex items-center" data-id="element-1338">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1339" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-1340">
                Jednorázová platba
              </span>
            </div>
            <div className="flex items-center" data-id="element-1341">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1342" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-1343">
                Získáš okamžitý přístup do emailu
              </span>
            </div>
          </div>

          {/* BHS Explainer - Expandable */}
          <BhsExplainer data-id="element-1344" />

          <ClientTransformationShowcase data-id="element-1345" />

          {showProgressSection && <motion.div initial={isMobile ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 40
        }} whileInView={isMobile ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 1,
          y: 0
        }} transition={isMobile ? {
          duration: 0
        } : {
          duration: 0.8,
          ease: 'easeOut'
        }} viewport={{
          once: true,
          margin: '-100px'
        }} className="relative max-w-5xl mx-auto mt-16 md:mt-24" data-id="element-1346">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-600/20 via-green-400/10 to-green-600/20 rounded-2xl blur-2xl opacity-50 pointer-events-none" data-id="element-1347" />

              <div className="relative bg-gray-900/90 backdrop-blur-xl border border-green-500/30 rounded-2xl p-8 md:p-16 text-center overflow-hidden shadow-2xl" data-id="element-1348">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent opacity-50" data-id="element-1349" />
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-1350" />
                <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-1351" />

                <div className="relative z-10 flex flex-col items-center" data-id="element-1352">
                  <motion.span initial={isMobile ? {
                opacity: 1,
                y: 0
              } : {
                opacity: 0,
                y: 20
              }} whileInView={isMobile ? {
                opacity: 1,
                y: 0
              } : {
                opacity: 1,
                y: 0
              }} transition={isMobile ? {
                duration: 0
              } : {
                delay: 0.2,
                duration: 0.6
              }} className="text-green-500 font-bold tracking-[0.2em] uppercase text-xs md:text-sm mb-4" data-id="element-1353">
                    Exkluzivní nabídka
                  </motion.span>

                  <motion.h3 initial={isMobile ? {
                opacity: 1,
                y: 0
              } : {
                opacity: 0,
                y: 20
              }} whileInView={isMobile ? {
                opacity: 1,
                y: 0
              } : {
                opacity: 1,
                y: 0
              }} transition={isMobile ? {
                duration: 0
              } : {
                delay: 0.3,
                duration: 0.6
              }} className="text-2xl md:text-4xl lg:text-5xl font-black text-white mb-2 leading-tight" data-id="element-1354">
                    Chceš aby tvůj progress byl{' '}
                    <br className="hidden md:block" data-id="element-1355" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400" data-id="element-1356">
                      ještě rychlejší?
                    </span>
                  </motion.h3>

                  <motion.p initial={isMobile ? {
                opacity: 1,
                y: 0
              } : {
                opacity: 0,
                y: 20
              }} whileInView={isMobile ? {
                opacity: 1,
                y: 0
              } : {
                opacity: 1,
                y: 0
              }} transition={isMobile ? {
                duration: 0
              } : {
                delay: 0.4,
                duration: 0.6
              }} className="text-xl md:text-3xl font-bold mt-4 mb-10 text-glow" data-id="element-1357">
                    <span className="highlight-text" data-id="element-1358">Mám pro tebe řešení!</span>
                  </motion.p>

                  <motion.div initial={isMobile ? {
                opacity: 1,
                scale: 1
              } : {
                opacity: 0,
                scale: 0.9
              }} whileInView={isMobile ? {
                opacity: 1,
                scale: 1
              } : {
                opacity: 1,
                scale: 1
              }} whileHover={{
                scale: 1.05
              }} transition={isMobile ? {
                duration: 0
              } : {
                delay: 0.5,
                duration: 0.4,
                type: 'spring',
                stiffness: 200
              }} data-id="element-1359">
                    <Button variant="primary" size="lg" onClick={scrollToSuperfoods} className="luxury-button text-base md:text-xl px-10 py-6 shadow-[0_0_30px_-5px_rgba(34,197,94,0.4)] hover:shadow-[0_0_50px_-10px_rgba(34,197,94,0.6)] border border-green-400/30" data-id="element-1360">
                      CHCI RYCHLEJŠÍ PROGRESS!
                    </Button>
                  </motion.div>

                  <motion.p initial={isMobile ? {
                opacity: 1
              } : {
                opacity: 0
              }} whileInView={isMobile ? {
                opacity: 1
              } : {
                opacity: 1
              }} transition={isMobile ? {
                duration: 0
              } : {
                delay: 0.7,
                duration: 0.6
              }} className="mt-6 text-gray-400 text-xs md:text-sm max-w-lg mx-auto" data-id="element-1361">
                    Objev tajemství, díky kterému jsem měl výsledky rychleji než
                    kamarádi, se kterými jsem začínal.
                  </motion.p>
                </div>
              </div>
            </motion.div>}
        </div>
      </div>
      <div className="section-divider divider-slant-up" data-id="element-1362"></div>
    </section>;
};